import type { GovernmentRole } from './roles';

/**
 * Instruments a person can be authorised to sign.
 *
 * Signing is not a permission. A permission answers "may this account perform
 * this action"; signing authority is an attestation about a *named human* on a
 * legal instrument, and it has to survive in the record long after the account
 * is gone. It gets its own table (`signing_authority`) and its own audit trail.
 *
 * This list is `FORM_KINDS` from `@munifocus/contracts-gov` plus two
 * instruments that are not forms: `entry-warrant`, which has its own
 * `gov_warrant` entity and contracts, and `by-law`, which is an instrument of
 * council rather than of a file. `contracts-gov` carries a spec asserting every
 * `FormKind` appears here, so the two cannot drift.
 */
export const SIGNABLE_KINDS = [
  'notice-to-comply',
  'discharge-certificate',
  'complaint-report',
  'inspection-report',
  'consent-to-enter',
  'courtesy-letter',
  'revenue-tax-inquiry',
  'legal-checklist',
  'council-resolution',
  'reimbursement-request',
  'affidavit-of-service',
  'appeal-committee-report',
  'entry-warrant',
  'by-law',
] as const;
export type SignableKind = (typeof SIGNABLE_KINDS)[number];

const SIGNABLE_SET: ReadonlySet<string> = new Set(SIGNABLE_KINDS);

export function isSignableKind(value: string): value is SignableKind {
  return SIGNABLE_SET.has(value);
}

/**
 * Instruments signed by the head of council rather than by any account.
 *
 * A by-law and a council resolution are acts of the elected body. Nobody on
 * council holds a Munifocus login — see `GOVERNMENT_ROLES` — so these two can
 * never be covered by `DEFAULT_SIGNING_AUTHORITY`, which is keyed by role. They
 * are signed by a named `council_member`, and `signing_authority` carries that
 * signatory instead of a `user_id`.
 *
 * The clerk still *produces* the document and records it; the signature block
 * names the mayor.
 */
export const COUNCIL_SIGNABLE_KINDS = ['council-resolution', 'by-law'] as const;
export type CouncilSignableKind = (typeof COUNCIL_SIGNABLE_KINDS)[number];

const COUNCIL_SIGNABLE_SET: ReadonlySet<string> = new Set(COUNCIL_SIGNABLE_KINDS);

/** True when an instrument is signed by council, not by an account-holder. */
export function isCouncilSignableKind(value: string): value is CouncilSignableKind {
  return COUNCIL_SIGNABLE_SET.has(value);
}

/**
 * Which instruments each municipal role may sign by default.
 *
 * Defaults only. A municipality that wants its deputy clerk signing Form 5
 * grants that person a `signing_authority` row from the Government Portal; the
 * clerk holds `signing:grant` and needs no help from Munifocus to do it.
 */
export const DEFAULT_SIGNING_AUTHORITY: Partial<Record<GovernmentRole, readonly SignableKind[]>> = {
  counsel: ['legal-checklist', 'affidavit-of-service'],
  // No `council-resolution` and no `by-law`: those are council's own signature,
  // held by a named council member. See `COUNCIL_SIGNABLE_KINDS`.
  clerk: [
    'notice-to-comply',
    'discharge-certificate',
    'reimbursement-request',
    'revenue-tax-inquiry',
    // The clerk is secretary to the Appeal Committee and puts the report before it.
    'appeal-committee-report',
  ],
  gov_supervisor: [
    'notice-to-comply',
    'discharge-certificate',
    'entry-warrant',
    'courtesy-letter',
    'consent-to-enter',
    'affidavit-of-service',
    'complaint-report',
    'inspection-report',
    'appeal-committee-report',
  ],
  gov_officer: [
    'notice-to-comply',
    'discharge-certificate',
    'entry-warrant',
    'courtesy-letter',
    'consent-to-enter',
    'affidavit-of-service',
    'complaint-report',
    'inspection-report',
  ],
};

/** True when a role carries default authority over an instrument. */
export function roleMaySign(role: GovernmentRole, kind: SignableKind): boolean {
  return (DEFAULT_SIGNING_AUTHORITY[role] ?? []).includes(kind);
}
